import React, { useMemo } from 'react';
import type { WorkflowComponent, WorkflowGuard } from '../../../types/workflow';
import { cardRefOf, collectFlags, guardKind, type EditableEdge, type EditableWorkflow, type GuardKind } from '../../../utils/workflowDsl';
import { ChipsInput, Field, FieldGroup, Pill, Section, StructuredField } from './WorkflowFormControls';

export interface WorkflowEdgeInspectorProps {
  wf: EditableWorkflow;
  edgeId: string;
  components: WorkflowComponent[];
  onPatch: (patch: Partial<EditableEdge>) => void;
  onDelete: () => void;
  onSelectState: (stateId: string) => void;
}

const GUARD_OPTIONS: Array<{ id: Exclude<GuardKind, 'empty'>; title: string; hint: string }> = [
  { id: 'always', title: 'Always', hint: 'Unconditional transition.' },
  { id: 'trigger', title: 'Exit trigger (inline)', hint: 'Named trigger with a confidence threshold, defined on this edge.' },
  { id: 'card', title: 'Trigger card (library)', hint: 'Reference a trigger card; its definition lives in the library.' },
  { id: 'flags_all', title: 'All flags present', hint: 'Fires when every listed flag is set.' },
  { id: 'flags_any', title: 'Any flag present', hint: 'Fires when at least one listed flag is set.' },
  { id: 'expression', title: 'Expression', hint: 'Free-form guard expression evaluated by the engine.' },
];

const WorkflowEdgeInspector: React.FC<WorkflowEdgeInspectorProps> = ({ wf, edgeId, components, onPatch, onDelete, onSelectState }) => {
  const edge = wf.edges.find((e) => e.id === edgeId);
  const flagSuggestions = useMemo(() => collectFlags(wf), [wf]);

  const triggerCards = useMemo(() => {
    const byName: Record<string, number[]> = {};
    components
      .filter((c) => c.kind === 'trigger')
      .forEach((c) => {
        if (!byName[c.name]) byName[c.name] = [];
        if (byName[c.name].indexOf(c.version) === -1) byName[c.name].push(c.version);
      });
    Object.keys(byName).forEach((n) => byName[n].sort((a, b) => b - a));
    return byName;
  }, [components]);
  const triggerNames = useMemo(() => Object.keys(triggerCards).sort(), [triggerCards]);

  if (!edge) return null;

  const kind = guardKind(edge.guard);
  const ref = cardRefOf(edge.guard);
  const serverPin = edge.guard?.on_exit_trigger?.ref?.version;

  const setGuard = (guard: WorkflowGuard, extra: Partial<EditableEdge> = {}) => onPatch({ guard, ...extra });

  const switchKind = (next: Exclude<GuardKind, 'empty'>) => {
    if (next === kind) return;
    const previousName = edge.guard?.on_exit_trigger?.name || ref?.name || `${edge.source}_to_${edge.target}`;
    switch (next) {
      case 'always':
        setGuard({ always: true }, { refPin: undefined });
        break;
      case 'trigger':
        setGuard(
          {
            on_exit_trigger: {
              name: previousName,
              min_confidence: edge.guard?.on_exit_trigger?.min_confidence ?? 0.7,
              description: edge.guard?.on_exit_trigger?.description ?? null,
            },
          },
          { refPin: undefined }
        );
        break;
      case 'card':
        setGuard({ on_exit_trigger_ref: { name: triggerNames.indexOf(previousName) !== -1 ? previousName : triggerNames[0] || previousName } }, { refPin: undefined });
        break;
      case 'flags_all':
        setGuard({ flags_all: edge.guard?.flags_any || [] }, { refPin: undefined });
        break;
      case 'flags_any':
        setGuard({ flags_any: edge.guard?.flags_all || [] }, { refPin: undefined });
        break;
      case 'expression':
        setGuard({ expression: edge.guard?.expression || '' }, { refPin: undefined });
        break;
      default:
        break;
    }
  };

  const cardVersions = ref ? triggerCards[ref.name] || [] : [];

  return (
    <div className="cwf-inspector">
      <div className="cwf-inspector__head">
        <div className="cwf-inspector__title">
          <i className="fas fa-long-arrow-alt-right" aria-hidden="true" />
          <span>Transition</span>
        </div>
        <div className="cwf-inspector__actions">
          <button type="button" className="cwf-btn cwf-btn--xs cwf-btn--danger" onClick={onDelete} title="Delete this transition">
            <i className="fas fa-trash" aria-hidden="true" />
          </button>
        </div>
      </div>

      <div className="cwf-edge-route">
        <button type="button" className="cwf-link cwf-link--mono" onClick={() => onSelectState(edge.source)}>
          {edge.source}
        </button>
        <i className="fas fa-arrow-right" aria-hidden="true" />
        <button type="button" className="cwf-link cwf-link--mono" onClick={() => onSelectState(edge.target)}>
          {edge.target}
        </button>
      </div>

      <Section title="Guard" defaultOpen summary={GUARD_OPTIONS.find((o) => o.id === kind)?.title || 'none'}>
        <div className="cwf-radios">
          {GUARD_OPTIONS.map((opt) => (
            <label key={opt.id} className="cwf-check" title={opt.hint}>
              <input type="radio" name={`cwf-guard-${edge.id}`} checked={kind === opt.id} onChange={() => switchKind(opt.id)} />
              <span>{opt.title}</span>
            </label>
          ))}
        </div>

        {kind === 'trigger' && edge.guard.on_exit_trigger ? (
          <>
            <div className="cwf-grid-2">
              <Field label="name">
                <input
                  className="cwf-input cwf-input--mono"
                  value={edge.guard.on_exit_trigger.name}
                  onChange={(e) => setGuard({ on_exit_trigger: { ...edge.guard.on_exit_trigger!, name: e.target.value } })}
                />
              </Field>
              <Field label="min_confidence" hint="0 … 1">
                <input
                  className="cwf-input"
                  type="number"
                  step={0.05}
                  min={0}
                  max={1}
                  value={edge.guard.on_exit_trigger.min_confidence}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setGuard({
                      on_exit_trigger: { ...edge.guard.on_exit_trigger!, min_confidence: Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0.7 },
                    });
                  }}
                />
              </Field>
            </div>
            <Field label="description">
              <textarea
                className="cwf-textarea"
                rows={2}
                value={edge.guard.on_exit_trigger.description || ''}
                onChange={(e) => setGuard({ on_exit_trigger: { ...edge.guard.on_exit_trigger!, description: e.target.value || null } })}
              />
            </Field>
          </>
        ) : null}

        {kind === 'card' && ref ? (
          <>
            <div className="cwf-grid-2">
              <Field label="card name" hint={triggerNames.length ? undefined : 'Library has no trigger cards — type the name manually.'}>
                <input
                  className="cwf-input cwf-input--mono"
                  list={`cwf-trigger-names-${edge.id}`}
                  value={ref.name}
                  onChange={(e) => setGuard({ on_exit_trigger_ref: { name: e.target.value } }, { refPin: undefined })}
                />
                <datalist id={`cwf-trigger-names-${edge.id}`}>
                  {triggerNames.map((n) => (
                    <option key={n} value={n} />
                  ))}
                </datalist>
              </Field>
              <Field
                label="pinned version"
                hint={
                  serverPin != null && edge.refPin == null
                    ? `Resolved to v${serverPin} when the graph was last applied. Sent without a version → latest at apply time.`
                    : 'Without a version the latest card is taken when the graph is applied.'
                }
              >
                <select
                  className="cwf-input"
                  value={edge.refPin == null ? '' : String(edge.refPin)}
                  onChange={(e) => onPatch({ refPin: e.target.value ? Number(e.target.value) : undefined })}
                >
                  <option value="">latest (resolved at apply)</option>
                  {cardVersions.map((v) => (
                    <option key={v} value={v}>
                      v{v}
                    </option>
                  ))}
                  {edge.refPin != null && cardVersions.indexOf(edge.refPin) === -1 ? <option value={edge.refPin}>v{edge.refPin}</option> : null}
                </select>
              </Field>
            </div>
            {edge.guard.on_exit_trigger ? (
              <p className="cwf-note">
                Expanded on the server as <code>{edge.guard.on_exit_trigger.name}</code> ≥ {edge.guard.on_exit_trigger.min_confidence}
                {edge.guard.on_exit_trigger.description ? ` — ${edge.guard.on_exit_trigger.description}` : ''}. These fields belong to the card and are not
                editable from the edge.
              </p>
            ) : null}
          </>
        ) : null}

        {kind === 'flags_all' || kind === 'flags_any' ? (
          <FieldGroup label={kind} hint="Replaces the guard entirely — a previous trigger/expression guard is not kept.">
            <ChipsInput
              value={(kind === 'flags_all' ? edge.guard.flags_all : edge.guard.flags_any) || []}
              onChange={(next) => setGuard(kind === 'flags_all' ? { flags_all: next } : { flags_any: next })}
              suggestions={flagSuggestions}
              placeholder="flag"
              emptyText="no flags — the guard is empty"
            />
          </FieldGroup>
        ) : null}

        {kind === 'expression' ? (
          <Field label="expression">
            <textarea
              className="cwf-textarea cwf-textarea--mono"
              rows={3}
              value={edge.guard.expression || ''}
              onChange={(e) => setGuard({ expression: e.target.value })}
            />
          </Field>
        ) : null}
      </Section>

      <Section title="Transition" defaultOpen summary={`${edge.type} · priority ${edge.priority}`}>
        <div className="cwf-grid-2">
          <Field label="type" hint="manual transitions are not counted as exit triggers">
            <input
              className="cwf-input"
              list={`cwf-edge-types-${edge.id}`}
              value={edge.type}
              onChange={(e) => onPatch({ type: e.target.value || 'conditional' })}
            />
            <datalist id={`cwf-edge-types-${edge.id}`}>
              <option value="conditional" />
              <option value="manual" />
            </datalist>
          </Field>
          <Field label="priority" hint="Lower value wins when several guards fire.">
            <input
              className="cwf-input"
              type="number"
              step={1}
              value={edge.priority}
              onChange={(e) => onPatch({ priority: Number.isFinite(Number(e.target.value)) ? Number(e.target.value) : 0 })}
            />
          </Field>
        </div>
        <Field label="reason">
          <input className="cwf-input" value={edge.reason || ''} onChange={(e) => onPatch({ reason: e.target.value || null })} />
        </Field>
        <FieldGroup label="applied_flags" hint="Flags raised when this transition is taken.">
          <ChipsInput
            value={edge.applied_flags || []}
            onChange={(next) => onPatch({ applied_flags: next })}
            suggestions={flagSuggestions}
            placeholder="flag"
            emptyText="none"
          />
        </FieldGroup>
        <StructuredField label="metadata" value={edge.metadata} onChange={(next) => onPatch({ metadata: (next as Record<string, unknown> | undefined) ?? null })} />
      </Section>

      <div className="cwf-inspector__foot">
        <Pill tone="neutral" title="Edges have no server identity; this id is local to the editor.">
          {edge.id}
        </Pill>
      </div>
    </div>
  );
};

export default WorkflowEdgeInspector;
